import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Target, Shield, Zap, Layers, GitGraph, TrendingUp } from 'lucide-react';

const contributions = [
    {
        icon: <Target className="w-8 h-8 text-blue-500" />,
        title: "Normative Control Framework",
        description: "Established a novel framework (NRCF) that integrates normative constraints with reinforcement learning to ensure safe autonomous network operations."
    },
    {
        icon: <Shield className="w-8 h-8 text-green-500" />,
        title: "Safety-First Architecture",
        description: "Introduced the 'Safety Valve' mechanism (Algorithm Result Evaluator) using ensemble anomaly detection to intercept and validate AI decisions before execution."
    },
    {
        icon: <Zap className="w-8 h-8 text-amber-500" />,
        title: "Stochastic Control",
        description: "Demonstrated effective control of stochastic autonomous functions (ANF) using a dual-layer approach of data integrity verification and output evaluation."
    },
    {
        icon: <TrendingUp className="w-8 h-8 text-teal-500" />,
        title: "Maturity Assessment",
        description: "Adapted medical-grade scoring systems (Glasgow Coma Scale, Apgar) to create new metrics (GANFS, MEGABITS) for assessing network autonomy maturity."
    },
    {
        icon: <GitGraph className="w-8 h-8 text-rose-500" />,
        title: "INFEROBIA Method",
        description: "Developed the INFERential OBservability Algorithm to categorize overall output as 'good' or 'bad' by analyzing data collection methods, algorithm hyperparameters, and counterfactual proposals using inference trees for explainable root cause analysis."
    },
    {
        icon: <Layers className="w-8 h-8 text-indigo-500" />,
        title: "Intelligent Layer",
        description: "Proposed the introduction of an 'Intelligent Layer' as a distinct, additional layer within the telco network architecture to orchestrate autonomous functions."
    }
];

export const ContributionSection = () => {
    return (
        <section className="py-24 px-8 bg-white relative z-10">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Contribution to Knowledge</h2>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                        Bridging the gap between theoretical AI autonomy and practical, safe network implementation.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {contributions.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
                        >
                            <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center shadow-sm mb-6 border border-gray-100">
                                {item.icon}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                            <p className="text-gray-600 leading-relaxed">
                                {item.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
